import { moNum } from "@/lib/moNum";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Eye, Trash2 } from "lucide-react";

const num = (n) => (n == null || isNaN(n) ? 0 : Number(n));
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(s + "T00:00:00");
  return isNaN(d) ? s : d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
};
const statusColor = (s) =>
  s === "Shortage" ? "text-red-600" : s === "Low" ? "text-amber-600" : "text-emerald-600";

const Info = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="text-slate-800">{value}</p>
  </div>
);

export default function MODetailModal({ detail, reports = [], onClose, onViewReport, onDeleteReport }) {
  const linked = detail ? reports.filter((r) => r.mwo_id === detail.id || (r.auto_allocations || []).some((a) => a.mwo_id === detail.id)) : [];
  const overdue = detail && detail.production_date && detail.status !== "Completed" &&
    detail.production_date < new Date().toISOString().slice(0, 10);
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-sm sm:max-w-2xl" data-testid="mo-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading tracking-tight">
            {moNum(detail?.mwo_number) || "Manufacturing Order"}
            {overdue && <span className="rounded-sm bg-red-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-red-700" data-testid="mo-detail-overdue">Overdue</span>}
          </DialogTitle>
          <DialogDescription className="text-left">{detail?.product_name}{detail?.design_name ? ` · ${detail.design_name}` : ""}</DialogDescription>
        </DialogHeader>
        {detail && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <Info label="Pack Size" value={`${detail.case_pack}-Pack`} />
              <Info label="Ordered (bottles)" value={`${num(detail.ordered_bottles || detail.bottle_quantity).toLocaleString()}`} />
              <Info label="Scheduled Date" value={fmtDate(detail.production_date)} />
              <Info label="Production Status" value={detail.status} />
              <Info label="Produced (bottles)" value={`${num(detail.produced_bottles).toLocaleString()}`} />
              <Info label="Remaining (bottles)" value={`${Math.max(0, num(detail.ordered_bottles || detail.bottle_quantity) - num(detail.produced_bottles)).toLocaleString()}`} />
              <Info label="Completion" value={`${num(detail.completion)}%`} />
              <Info label="Linked Reports" value={String((detail.linked_report_ids || []).length)} />
            </div>

            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Order BOM (snapshot)</p>
              <table className="w-full table-fixed border-collapse text-xs">
                <thead>
                  <tr className="text-[9px] uppercase text-slate-500">
                    <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left">Component</th>
                    <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left">Type</th>
                    <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-right">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.bom_snapshot || []).map((b, i) => (
                    <tr key={`${b.name}-${b.type || ""}-${i}`}>
                      <td className="border border-slate-300 px-2 py-1 font-semibold text-slate-800">{b.name}</td>
                      <td className="border border-slate-300 px-2 py-1 text-slate-500">{b.type || "—"}</td>
                      <td className="border border-slate-300 px-2 py-1 text-right text-slate-600">{b.display}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Inventory Availability (at time of order)</p>
              <table className="w-full border-collapse text-xs">
                <thead>
                  <tr className="text-[9px] uppercase text-slate-500">
                    <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left">Component</th>
                    <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-right">Required</th>
                    <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-right">Available</th>
                    <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-right">Shortage</th>
                    <th className="border border-slate-300 bg-slate-100 px-2 py-1 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(detail.availability || []).map((a, i) => (
                    <tr key={`${a.name}-${a.type || ""}-${i}`} className={a.status === "Shortage" ? "bg-red-50" : ""} data-testid={`mo-detail-avail-row-${i}`}>
                      <td className={`border border-slate-300 px-2 py-1 ${a.status === "Shortage" ? "font-semibold text-red-700" : "text-slate-800"}`}>{a.status === "Shortage" && <AlertTriangle className="mr-1 inline h-3 w-3 text-red-600" />}{a.name}{a.type ? ` · ${a.type}` : ""}</td>
                      <td className="border border-slate-300 px-2 py-1 text-right text-slate-600">{num(a.required).toLocaleString()}</td>
                      <td className={`border border-slate-300 px-2 py-1 text-right ${a.status === "Shortage" ? "font-semibold text-red-700" : "text-slate-600"}`}>{num(a.available).toLocaleString()}</td>
                      <td className={`border border-slate-300 px-2 py-1 text-right ${a.shortage > 0 ? "font-semibold text-red-700" : "text-slate-600"}`}>{num(a.shortage).toLocaleString()}</td>
                      <td className={`border border-slate-300 px-2 py-1 font-semibold ${statusColor(a.status)}`}>{a.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Associated Production Reports</p>
              {linked.length === 0 ? (
                <p className="text-xs text-slate-400">No production reports yet. Enter production to create one.</p>
              ) : (
                <div className="divide-y divide-slate-100 border border-slate-200">
                  {linked.map((r) => (
                    <div key={r.id} className="flex items-center justify-between px-3 py-2 text-xs" data-testid={`mo-detail-report-${r.id}`}>
                      <div className="text-slate-700">
                        <span className="font-semibold text-slate-900">{r.date || "—"}</span>
                        <span className="ml-2 text-slate-500">{Math.round(r.total_stockable_bottles || 0).toLocaleString()} bottles · {r.efficiency != null ? `${r.efficiency}%` : "—"} eff.</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="sm" className="h-7 rounded-sm text-[#002FA7]" onClick={() => onViewReport?.(r.id)} data-testid={`mo-detail-view-report-${r.id}`}>
                          <Eye className="mr-1 h-3.5 w-3.5" /> View
                        </Button>
                        {onDeleteReport && (
                          <Button variant="ghost" size="sm" className="h-7 rounded-sm text-slate-400 hover:text-red-600" onClick={() => { if (window.confirm(`Delete this production report? Its inventory movements will be reversed and the order progress recalculated.`)) onDeleteReport(r.id); }} data-testid={`mo-detail-delete-report-${r.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onClose?.()} data-testid="mo-detail-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
